// client/src/components/pages/RouteSummary/summaryFigures.ts
//
// ST-00218 P3: what the Route Summary table shows besides the per-day rows, from the
// `figures` / `matching` blocks of POST /route-summary/generate (scratchpad/st00218/API_P3.md
// §4.2). Pure, so the table and its CSV export read the same numbers.
//
// On a "Truck + time" route (figures.method engine / mixed) the dispatch bottom line is the
// server's adjusted dispatched:
//   Σ daily dispatched + Brought Forward − Carried Forward − received off-route − no receipt data
// Missing and needs-review loads stay in Dispatched: they are listed, not deducted. An older
// server (or a cached answer) sends no `figures`: the table keeps its old arithmetic.
// ST-00298: the bottom line's variance needs an adjusted dispatch above 0.5 t (utils/variance);
// receipts against nothing left dispatched (every dispatch carried forward) read n/a.

import type {
  BfcfVariant,
  MatchedVarianceInfo,
  RouteSummaryDayRow,
  RouteSummaryForwardRow,
  RouteSummaryGroupingInfo,
} from '../../../types/clientPages/routeSummary';
import type { MatchingInfo, RouteFigures } from '../../../types/routeFigures';
import { lacksVarianceBase, NO_VARIANCE_BASE_TEXT, varianceOf } from '../../../utils/variance';

/** True when the figures came (at least partly) from the load-match ledger. */
export const isEngineFigures = (figures: RouteFigures | null | undefined): boolean =>
  !!figures && (figures.method === 'engine' || figures.method === 'mixed');

/** One row the table shows after Carried Forward on a Truck + time route. */
export interface FigureRow {
  variant: Exclude<BfcfVariant, 'brought-forward' | 'carried-forward'>;
  label: string;
  /** The second line under the label. */
  sublabel: string;
  /** Plain words for the tooltip / screen reader. */
  explanation: string;
  weight: number;
  count: number;
  /** Deducted from the dispatch side (like CF); otherwise information only. */
  deducted: boolean;
  /** Which side's columns hold the figure (unmatched receipts sit under Receipt). */
  side?: 'dispatch' | 'receipt';
}

/** The engine-only rows with something in them, in table order. */
export function figureRows(figures: RouteFigures | null | undefined): FigureRow[] {
  if (!isEngineFigures(figures)) return [];
  const f = figures as RouteFigures;
  const rows: FigureRow[] = [
    {
      variant: 'received-off-route',
      label: 'Received off-route',
      sublabel: 'Received at a site not on this route',
      explanation:
        'Dispatched in the period and received on a site that is not on this route. Deducted from the dispatch total, like Carried Forward.',
      weight: f.receivedOffRouteWeight,
      count: f.receivedOffRouteCount,
      deducted: true,
    },
    {
      variant: 'no-receipt-data',
      label: 'No receipt data',
      sublabel: 'Not reconciled: no receipt feed',
      explanation:
        'Dispatched in the period while the route has no receipt data for that time. Not reconciled, so deducted from the dispatch total.',
      weight: f.noReceiptDataWeight,
      count: f.noReceiptDataCount,
      deducted: true,
    },
    {
      variant: 'missing',
      label: 'Missing loads',
      sublabel: 'Included in Dispatched',
      explanation:
        'Dispatched, not received and past the route’s transit window. They stay in the dispatch total, so the variance shows them.',
      weight: f.missingWeight,
      count: f.missingCount,
      deducted: false,
    },
    {
      variant: 'needs-review',
      label: 'Needs review',
      sublabel: 'Included in Dispatched',
      explanation:
        'The truck was dispatched again before a receipt for this load. Check which dispatch is real. They stay in the dispatch total.',
      weight: f.needsReviewWeight,
      count: f.needsReviewCount,
      deducted: false,
    },
    {
      variant: 'unmatched-receipts',
      label: 'Receipts with no dispatch',
      sublabel: 'Included in Received',
      explanation:
        'Received in the period with no dispatch on this route: no dispatch found, a load dispatched on another route, or no dispatch data. They stay in the receipt total.',
      weight: Number(f.unmatchedReceiptWeight) || 0,
      count: Number(f.unmatchedReceiptCount) || 0,
      deducted: false,
      side: 'receipt',
    },
  ];
  return rows.filter((r) => (Number(r.count) || 0) > 0);
}

/** The bottom line of the table (dispatch side adjusted, receipts as received). */
export interface SummaryTotals {
  dispatchWeight: number;
  dispatchCount: number;
  receiptWeight: number;
  receiptCount: number;
}

export function summaryTotals(
  data: RouteSummaryDayRow[],
  preload: RouteSummaryForwardRow | null | undefined,
  carriedForward: RouteSummaryForwardRow | null | undefined,
  figures: RouteFigures | null | undefined
): SummaryTotals {
  if (isEngineFigures(figures)) {
    const f = figures as RouteFigures;
    return {
      dispatchWeight: f.adjustedDispatchWeight,
      dispatchCount: f.adjustedDispatchCount,
      receiptWeight: f.receivedWeight,
      receiptCount: f.receivedCount,
    };
  }
  // The arithmetic of before P3 (legacy methods, or an answer without `figures`).
  const bfW = preload ? preload.dispatchWeight : 0;
  const bfC = preload ? preload.dispatchCount : 0;
  const cfW = carriedForward ? carriedForward.dispatchWeight : 0;
  const cfC = carriedForward ? carriedForward.dispatchCount : 0;
  return data.reduce(
    (acc, row) => ({
      dispatchWeight: acc.dispatchWeight + row.dispatchWeight,
      dispatchCount: acc.dispatchCount + row.dispatchCount,
      receiptWeight: acc.receiptWeight + row.receiptWeight,
      receiptCount: acc.receiptCount + row.receiptCount,
    }),
    { dispatchWeight: bfW - cfW, dispatchCount: bfC - cfC, receiptWeight: 0, receiptCount: 0 }
  );
}

/**
 * The bottom line's weight variance in %, (received − dispatched) / dispatched over the adjusted
 * dispatch: 0 with nothing on either side, null = n/a when receipts stand against nothing left
 * dispatched (ST-00298).
 */
export const summaryVariancePct = (totals: SummaryTotals): number | null =>
  varianceOf(totals.receiptWeight, totals.dispatchWeight);

/** The variance is n/a: the server says so and the route lacks receipt or dispatch data. */
export const varianceNotAvailable = (figures: RouteFigures | null | undefined): boolean =>
  !!figures &&
  figures.variancePct === null &&
  figures.dataStatus !== null &&
  figures.dataStatus !== 'ok';

/**
 * Why the variance is n/a, naming the sites (null when it is not). With the bottom line
 * (`totals`): also nothing left dispatched to compare (ST-00298).
 */
export function notAvailableHint(
  figures: RouteFigures | null | undefined,
  grouping: Pick<RouteSummaryGroupingInfo, 'dispatchLocations' | 'receiptLocations'>,
  totals?: SummaryTotals | null
): string | null {
  if (!varianceNotAvailable(figures)) {
    return totals && lacksVarianceBase(totals.receiptWeight, totals.dispatchWeight)
      ? NO_VARIANCE_BASE_TEXT
      : null;
  }
  const f = figures as RouteFigures;
  if (f.dataStatus === 'no_dispatch_data') {
    return `No dispatch data from ${grouping.dispatchLocations.join(', ')}`;
  }
  if (f.dataStatus === 'empty') return 'No loads in this period';
  return `No receipt data from ${grouping.receiptLocations.join(', ')}`;
}

/** The method words of the per-day sub-label. */
const MODE_WORDS: Record<string, string> = {
  engine: 'truck + time',
};

/**
 * The sub-label under "Matched Variance". Legacy modes keep the text of before
 * ("matched on receipt date · exact ticket match"); Truck + time reads "… · truck + time".
 */
export function matchedSubLabel(mv: MatchedVarianceInfo): string {
  if (mv.available !== true) return 'period total';
  const modes = Array.isArray(mv.modes) ? (mv.modes.filter(Boolean) as string[]) : [];
  if (modes.length === 0) return 'matched on receipt date';
  if (!modes.includes('engine')) return `matched on receipt date · ${modes.join('/')} ticket match`;
  const words = modes.map((m) => MODE_WORDS[m] || `${m} ticket match`);
  return `matched on receipt date · ${words.join(' / ')}`;
}

/** The fallback note as one line (screen and CSV). */
export function fallbackNoteText(matching: MatchingInfo | null | undefined): string | null {
  if (!matching || !matching.fallback || !matching.note) return null;
  return matching.detail ? `${matching.note} — ${matching.detail}` : matching.note;
}
